import {
  DeviceType,
  EPropertyType,
  ESymbolCategory,
  IAction,
  IActionDefinitionDto,
  IPropertyEnums,
  IProperty,
  IPropertyTypeOptions,
  ISymbol,
  PropertyToolType
} from "@kuika/kuika-cl-model";
import { toValueLabel } from "./quick-labels";

/**
 * Pure rules of the selection bar's quick props: which control a property gets, the text its chip
 * shows, and the small value conversions. No React, no sd — the region feeds these the resolved
 * `IProperty` and the tool type `PropertyHelper.calculatePropertyToolType` computed for it, the same
 * two inputs the property panel's `PropertyFormItem` branches on.
 */

export type QuickControlKind =
  /** Inline text input. */
  | "text"
  /** Chip with the current option → popover list. */
  | "choice"
  | "number"
  | "boolean"
  /** Swatch → the panel's own editor in a popover. */
  | "color"
  /** A Col responsive span (xs…xxl): read-only here, edited through the row in the panel. */
  | "column"
  /** Summary chip → the property panel's own editor (`PropertyFormItem`) in a popover. */
  | "editor";

/** Symbol categories that hold a value picked in the editor itself, not a binding to something else. */
const VALUE_CATEGORIES: ReadonlySet<ESymbolCategory> = new Set<ESymbolCategory>([
  ESymbolCategory.none,
  ESymbolCategory.fixed,
  ESymbolCategory.default,
  ESymbolCategory.uploadedImages,
  ESymbolCategory.icons
]);

/** Properties the panel edits through its data editor whatever their tool type (see PropertyFormItem). */
const DATA_EDITOR_NAMES: ReadonlySet<string> = new Set<string>([
  "defaultDataField",
  "rowKey",
  "dynamicFontColor",
  "dynamicBackGroundColor",
  "dynamicBgImage"
]);

/** True when the property's current symbol binds it to something (a field, an action result, …). */
export function isBoundSymbol(symbol: ISymbol | undefined | null): boolean {
  if (!symbol || symbol.category === undefined || symbol.category === null) {
    return false;
  }
  if (symbol.isFixed === true) {
    return false;
  }
  return !VALUE_CATEGORIES.has(symbol.category);
}

/**
 * True when the symbol holds a plain typed-in value — the case the panel's plain writer
 * (`setDesignerPropertyValue`, which stores `category: fixed` + `fixedValue`) handles. A value prop
 * (Label content, Button label) holding one is typed straight into the bar; bound, it stays a chip.
 */
export function isFixedValueSymbol(symbol: ISymbol | undefined | null): boolean {
  if (!symbol) {
    return true;
  }
  if (symbol.actionDefinitionId || symbol.actionDefinitionObjectPath) {
    return false;
  }
  if (symbol.isFixed === true) {
    return true;
  }
  return (
    symbol.category === undefined ||
    symbol.category === null ||
    symbol.category === ESymbolCategory.none ||
    symbol.category === ESymbolCategory.fixed ||
    symbol.category === ESymbolCategory.default
  );
}

/** True for a property the bar must never surface, whatever the curated list says. */
export function isHiddenProperty(property: IProperty | undefined): boolean {
  return (
    property === undefined ||
    property.isTechnical === true ||
    property.isDeprecated === true ||
    property.selectedType === EPropertyType.event
  );
}

/**
 * Picks the control, mirroring `PropertyFormItem.renderFormItem`'s precedence: anything the panel edits
 * through its data editor or a bespoke editor goes to the editor popover, a bound value shows its binding
 * (and opens the editor), and only plain values get an inline control. A value prop (Label content,
 * Button label, TextInput value) holding a typed-in string is one of those plain values — typed straight
 * into the bar, with the editor (and binding) one "Bind to data" click away.
 */
export function classifyProperty(
  property: IProperty,
  toolType: PropertyToolType,
  isDataEditorProperty: boolean
): QuickControlKind {
  if (toolType === "ResponsiveColumn") {
    return "column";
  }
  const isTypedValueProp: boolean =
    property.isValueProp === true && toolType === "String" && isFixedValueSymbol(property.symbol);
  if (
    isDataEditorProperty ||
    (property.isValueProp === true && !isTypedValueProp) ||
    DATA_EDITOR_NAMES.has(property.name) ||
    toolType === "NestedTableOptions" ||
    isBoundSymbol(property.symbol)
  ) {
    return "editor";
  }
  switch (toolType) {
    case "String":
      return "text";
    case "Select":
    case "Enum":
      return "choice";
    case "Number":
      return "number";
    case "Boolean":
      return "boolean";
    case "ColorPicker":
      return "color";
    default:
      return "editor";
  }
}

export interface IChoiceOption {
  value: string;
  label: string;
}

/**
 * `Select` options (`property.options`, narrowed by `optionsDependsOn` like the panel does). The label is
 * the friendly form (`toValueLabel`); the value — what a pick writes — stays the raw option.
 */
export function getSelectOptions(property: IProperty, siblingValue: (name: string) => unknown): IChoiceOption[] {
  let options: string[] = property.options || [];
  const dependsOn: string | undefined = property.optionsDependsOn;
  const byDependency: Record<string, string[]> | undefined = property.optionsByDependency;
  if (dependsOn && byDependency) {
    const raw: unknown = siblingValue(dependsOn);
    const key: string = typeof raw === "string" && raw.length > 0 ? raw : property.defaultValue;
    const filtered: string[] | undefined = byDependency[key];
    if (Array.isArray(filtered) && filtered.length > 0) {
      options = filtered;
    }
  }
  return options.map((o: string): IChoiceOption => ({ value: o, label: toValueLabel(o) }));
}

/** `Enum` options: `typeOptions[propertyType = enum].enums`, label = key, value = value (the panel's rule). */
export function getEnumOptions(property: IProperty): IChoiceOption[] {
  const enumType: IPropertyTypeOptions | undefined = property.typeOptions?.find(
    (t: IPropertyTypeOptions): boolean => t.propertyType === EPropertyType.enum.toString()
  );
  return (enumType?.enums ?? []).map((e: IPropertyEnums): IChoiceOption => ({
    value: e.value,
    label: toValueLabel(e.key)
  }));
}

/**
 * What a choice property's stored value reads as outside its option list (a group summary): the enum
 * key when it is an enum value, else the friendly form of the value itself.
 */
export function choiceValueLabel(property: IProperty, toolType: PropertyToolType, value: string): string {
  if (toolType === "Enum") {
    const option: IChoiceOption | undefined = getEnumOptions(property).find(
      (o: IChoiceOption): boolean => o.value === value
    );
    if (option) {
      return option.label;
    }
  }
  return toValueLabel(value);
}

/** A value the panel treats as "not set". */
export function isEmptyValue(value: unknown): boolean {
  if (value === undefined || value === null) {
    return true;
  }
  if (typeof value === "string") {
    return value.trim() === "" || value === "-1";
  }
  if (Array.isArray(value)) {
    return value.length === 0;
  }
  return false;
}

export function toBoolean(value: unknown): boolean {
  return value === true || value === "true";
}

export function toNumber(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed: number = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
  }
  return null;
}

export interface ISymbolSummaryLookups {
  actionDefinitions: IActionDefinitionDto[] | undefined;
  definedActions: IAction[] | undefined;
}

/**
 * A short label for what a bound symbol points at: the field, the action result, the screen input…
 * Falls back to the symbol's own label/description with the `[…]` placeholders the panel substitutes
 * stripped out.
 */
export function summarizeSymbol(symbol: ISymbol, lookups: ISymbolSummaryLookups): string {
  if (symbol.resultActionId) {
    const action: IAction | undefined = lookups.definedActions?.find(
      (a: IAction): boolean => a.id === symbol.resultActionId
    );
    if (action) {
      const base: string = action.tag ? `${action.label}#${action.tag}` : action.label;
      return symbol.fieldName ? `${base}.${symbol.fieldName}` : base;
    }
  }
  if (symbol.fieldName) {
    return symbol.fieldName;
  }
  if (symbol.inputName) {
    return symbol.inputName;
  }
  if (symbol.actionDefinitionId) {
    const def: IActionDefinitionDto | undefined = lookups.actionDefinitions?.find(
      (d: IActionDefinitionDto): boolean => d.id === symbol.actionDefinitionId
    );
    if (def) {
      return def.displayName ?? def.name;
    }
  }
  if (symbol.actionDefinitionObjectPath) {
    return symbol.actionDefinitionObjectPath;
  }
  const text: string = (symbol.label || symbol.description || "").replace(/\[[^\]]*\]/g, "").trim();
  return text || "Bound";
}

/** What an editor chip shows for a property. */
export interface IPropertySummary {
  text: string;
  /**
   * The placeholder rows a data component ships with (a Table's default `dataSource`), not a real
   * source: shown muted, and counted as "not set" for the chip's warn dot.
   */
  isSample: boolean;
  /** The unabridged wording for the tooltip when `text` is a shortened form ("Sample data · 3 rows"). */
  fullText?: string;
}

/**
 * Item count of an array held as text — JSON, or the JS-literal shape component defaults use
 * (`[ { key: "1", name: "…" } ]`, unquoted keys). Counts top-level elements, ignoring commas inside
 * strings and nested brackets. Null when the text is not an array at all.
 */
export function countArrayLiteralItems(text: string): number | null {
  const trimmed: string = text.trim();
  if (!trimmed.startsWith("[") || !trimmed.endsWith("]")) {
    return null;
  }
  const inner: string = trimmed.slice(1, -1);
  if (inner.trim() === "") {
    return 0;
  }
  let depth: number = 0;
  let quote: string | null = null;
  let count: number = 1;
  let isAfterSeparator: boolean = false;
  for (let i = 0; i < inner.length; i++) {
    const ch: string = inner[i];
    if (quote !== null) {
      if (ch === "\\") {
        i++;
      } else if (ch === quote) {
        quote = null;
      }
      continue;
    }
    if (ch === '"' || ch === "'" || ch === "`") {
      quote = ch;
      isAfterSeparator = false;
    } else if (ch === "[" || ch === "{" || ch === "(") {
      depth++;
      isAfterSeparator = false;
    } else if (ch === "]" || ch === "}" || ch === ")") {
      depth--;
    } else if (ch === "," && depth === 0) {
      count++;
      isAfterSeparator = true;
    } else if (ch.trim() !== "") {
      isAfterSeparator = false;
    }
  }
  // A trailing comma (`[a, b,]`) does not open another element.
  return isAfterSeparator ? count - 1 : count;
}

/** The property a data component reads its rows from; its fixed value is sample data. */
const DATA_SOURCE_PROPERTY_NAME: string = "dataSource";

function summarizeList(property: IProperty, count: number): IPropertySummary {
  if (property.name === DATA_SOURCE_PROPERTY_NAME) {
    const rows: string = `${count} row${count === 1 ? "" : "s"}`;
    // Short in the chip (the bar is a strip); the tooltip spells it out.
    return { text: `Sample · ${rows}`, isSample: true, fullText: `Sample data · ${rows}` };
  }
  return { text: `${count} item${count === 1 ? "" : "s"}`, isSample: false };
}

/** The chip text of a property shown in the editor popover (datasource, image, icon, options…). */
export function summarizeProperty(property: IProperty, lookups: ISymbolSummaryLookups): IPropertySummary | null {
  const symbol: ISymbol | undefined = property.symbol;
  if (!symbol) {
    return null;
  }
  if (isBoundSymbol(symbol) || symbol.actionDefinitionId || symbol.actionDefinitionObjectPath) {
    return { text: summarizeSymbol(symbol, lookups), isSample: false };
  }
  const value: unknown = symbol.fixedValue;
  if (isEmptyValue(value)) {
    return null;
  }
  if (symbol.category === ESymbolCategory.uploadedImages) {
    return { text: "Image", isSample: false };
  }
  if (Array.isArray(value)) {
    return summarizeList(property, value.length);
  }
  if (typeof value === "object") {
    return { text: "Configured", isSample: false };
  }
  if (typeof value === "string") {
    const count: number | null = countArrayLiteralItems(value);
    if (count !== null) {
      return summarizeList(property, count);
    }
  }
  return { text: String(value), isSample: false };
}

/**
 * One value inside a summary ("On · 10"): booleans read On/Off, numbers plain, anything else as text.
 * Null when there is nothing to show.
 */
export function formatSummaryValue(kind: QuickControlKind, value: unknown): string | null {
  if (kind === "boolean") {
    return toBoolean(value) ? "On" : "Off";
  }
  if (isEmptyValue(value)) {
    return null;
  }
  if (kind === "number") {
    const n: number | null = toNumber(value);
    return n === null ? null : String(n);
  }
  return String(value);
}

// ── Col widths (propertyGroup of xs…xxl) ────────────────────────────────────────────────────────

const DEVICE_COLUMNS: Record<DeviceType, readonly string[]> = {
  [DeviceType.Desktop]: ["xl", "xxl"],
  [DeviceType.Tablet]: ["lg", "md", "sm"],
  [DeviceType.Phone]: ["xs"]
};

/** Group members reordered so the breakpoints of the device the canvas shows come first. */
export function orderByDevice(names: string[], device: DeviceType | undefined): string[] {
  const first: readonly string[] = device !== undefined ? (DEVICE_COLUMNS[device] ?? []) : [];
  return [
    ...names.filter((n: string): boolean => first.includes(n)),
    ...names.filter((n: string): boolean => !first.includes(n))
  ];
}

/** A Col span as stored (`"8"`, or with a text prefix/suffix) → its number. */
export function parseColumnSpan(value: unknown): number | null {
  if (typeof value === "number") {
    return value;
  }
  if (typeof value !== "string") {
    return null;
  }
  const match: RegExpMatchArray | null = value.match(/([0-9]+)/);
  return match ? parseInt(match[1], 10) : null;
}
